import classes from './Filtersection.module.scss';
import { Button } from 'primereact/button';
import { useFilterSection } from './useFilterSection.logic';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { FilterSectionProps } from 'Interfaces/FDPInterfaces/unconstrainedForecastBulkEdit';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import AsyncSelect from 'react-select/async';
import { X } from 'lucide-react';

const FilterSection: React.FC<FilterSectionProps> = ({
  filterpayload,
  isDataLoading,
  areChangesToBeSaved,
  handleApplyFilters,
}) => {
  const {
    RampingOptions,
    StratCodeOptions,
    SellingCategoryOptions,
    ContractTypeOptions,
    isAllMoleculesLoading,
    filteredMolecules,
    isAllConceptLoading,
    filteredConcept,
    isAllSupplierNamesLoading,
    filteredSupplierNames,
    filter,
    setFilter: _setFilter,
    setFilterValues,
    handleFilter,
    handlerFunctions,
    getfilterOption,
    handleRestFilter,
    handleApplyFilterButtonClick,
    isFilterButtonDisabled,
    applyFilterConfirmationModalVisibility,
    toggleApplyFilterConfirmationModalVisibility,
  } = useFilterSection({
    filterpayload,
    handleApplyFilters,
    isDataLoading,
    areChangesToBeSaved,
  });

  return (
    <div className={classes.container}>

      {/* 2 rows × 4 columns — all 8 filters */}
      <div className={classes.filterGrid}>

        {/* Row 1 */}
        <div className={classes.filterItem}>
          <label htmlFor="ramping" className={classes.inputLabel}>Ramping:</label>
          <Dropdown
            inputId="ramping"
            placeholder="Yes/No..."
            className={classes.dropDownBox}
            value={filter.Ramping}
            options={RampingOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            onChange={e => setFilterValues('Ramping', e.target.value ?? '')}
          />
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="molecule" className={classes.inputLabel}>Molecule:</label>
          <div className={classes.clearableWrapper}>
            <AutoComplete
              onSelect={e => setFilterValues('MoleculeAndDosageForm', e.value)}
              autoHighlight={true}
              delay={100}
              loadingIcon={null}
              onDropdownClick={() => console.log('clicked')}
              disabled={isAllMoleculesLoading}
              placeholder="Search a Molecule...."
              style={{ flexGrow: 1 }}
              className={classes.inputBox}
              value={filter.MoleculeAndDosageForm}
              suggestions={filteredMolecules}
              dropdownIcon={null}
              completeMethod={handlerFunctions.handleFilterMolecules}
              onChange={e => setFilterValues('MoleculeAndDosageForm', e.value)}
            />
            {filter.MoleculeAndDosageForm && (
              <button
                type="button"
                className={classes.autoCompleteClearBtn}
                onClick={() => setFilterValues('MoleculeAndDosageForm', '')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="sellingCategory" className={classes.inputLabel}>Selling Category:</label>
          <Dropdown
            inputId="sellingCategory"
            placeholder="Select Selling category..."
            className={classes.dropDownBox}
            value={filter.SellingCategory}
            options={SellingCategoryOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            onChange={e => setFilterValues('SellingCategory', e.target.value ?? '')}
          />
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="concept" className={classes.inputLabel}>Concept:</label>
          <div className={classes.clearableWrapper}>
            <AutoComplete
              inputId="concept"
              onSelect={e => setFilterValues('Concept', e.value)}
              autoHighlight={true}
              delay={100}
              loadingIcon={null}
              disabled={isAllConceptLoading}
              placeholder="Search a concept...."
              style={{ flexGrow: 1 }}
              className={classes.inputBox}
              value={filter.Concept}
              suggestions={filteredConcept}
              dropdownIcon={null}
              completeMethod={handlerFunctions.handleFilterConcepts}
              onChange={e => setFilterValues('Concept', e.value)}
            />
            {filter.Concept && (
              <button
                type="button"
                className={classes.autoCompleteClearBtn}
                onClick={() => setFilterValues('Concept', '')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Row 2 */}
        <div className={classes.filterItem}>
          <label htmlFor="stratCode" className={classes.inputLabel}>Strategy Code:</label>
          <Dropdown
            inputId="stratCode"
            placeholder="Select Strategy Code..."
            className={classes.dropDownBox}
            value={filter.StrategyCode}
            options={StratCodeOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            onChange={e => setFilterValues('StrategyCode', e.target.value ?? '')}
          />
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="ndc" className={classes.inputLabel}>NDC:</label>
          <AsyncSelect
            inputId="ndc"
            loadOptions={getfilterOption.fetchNDCSuggestions}
            onChange={option => {
              setFilterValues('NDC11', option?.value || '');
            }}
            defaultOptions
            isClearable
            placeholder="Search for NDC11..."
            value={filter.NDC11 ? { label: filter.NDC11, value: filter.NDC11 } : null}
            styles={{
              menu: provided => ({ ...provided, zIndex: 20 }),
            }}
          />
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="supplierName" className={classes.inputLabel}>Supplier Name:</label>
          <div className={classes.clearableWrapper}>
            <AutoComplete
              inputId="supplierName"
              onSelect={e => setFilterValues('SupplierName', e.value)}
              autoHighlight={true}
              delay={100}
              loadingIcon={null}
              disabled={isAllSupplierNamesLoading}
              placeholder="Search a Supplier Name...."
              style={{ flexGrow: 1 }}
              className={classes.inputBox}
              value={filter.SupplierName}
              suggestions={filteredSupplierNames}
              dropdownIcon={null}
              completeMethod={handlerFunctions.handleFilterSupplierNames}
              onChange={e => setFilterValues('SupplierName', e.value)}
            />
            {filter.SupplierName && (
              <button
                type="button"
                className={classes.autoCompleteClearBtn}
                onClick={() => setFilterValues('SupplierName', '')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className={classes.filterItem}>
          <label htmlFor="contractType" className={classes.inputLabel}>Contract Type:</label>
          <Dropdown
            inputId="contractType"
            placeholder="Select Contract Type..."
            className={classes.dropDownBox}
            value={filter.ContractType}
            options={ContractTypeOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            onChange={e => setFilterValues('ContractType', e.target.value ?? '')}
          />
        </div>

      </div>

      {/* Action buttons */}
      <div className={classes.btnComponent}>
        <div className={classes.btnContainer}>
          <Button
            className={classes.btn}
            style={{
              backgroundColor: 'rgb(247 243 254)',
              color: '#4d2e6d',
              border: '1px solid #4b5563',
            }}
            onClick={handleRestFilter}
          >
            Clear Filter
          </Button>
          <Button
            className={classes.btn}
            onClick={handleApplyFilterButtonClick}
            disabled={isFilterButtonDisabled()}
            loading={isDataLoading}
          >
            Apply
          </Button>
        </div>
      </div>

      <ConfirmationModal
        visibility={applyFilterConfirmationModalVisibility}
        modalHeading="Confirmation"
        modalContent="Changes are not saved, Are you sure you want to apply filter ?"
        loading={false}
        confirmLabel="Confirm"
        handleCancel={toggleApplyFilterConfirmationModalVisibility}
        handleConfirm={handleFilter}
      />
    </div>
  );
};

export default FilterSection;
